import { QueryClient, QueryFunction } from '@tanstack/react-query';

// 429 Circuit Breaker + Request Throttling (Global)
// Spec: on 429 => isThrottled=true, show toast, pause new fetches for exactly 5000ms.
const RATE_LIMIT_COOLDOWN_MS = 5000;

type ThrottleSnapshot = {
  isThrottled: boolean;
  throttleUntilMs: number | null;
  lastUrl?: string;
};

let throttleUntilMs: number | null = null;
let lastThrottleUrl: string | undefined;
let resumeTimer: ReturnType<typeof setTimeout> | null = null;
let throttleWaiter: Promise<void> | null = null;
let throttleWaiterResolve: (() => void) | null = null;
const throttleSubscribers = new Set<(snapshot: ThrottleSnapshot) => void>();

export function getThrottleSnapshot(): ThrottleSnapshot {
  const now = Date.now();
  const active = throttleUntilMs !== null && now < throttleUntilMs;
  return {
    isThrottled: active,
    throttleUntilMs: active ? throttleUntilMs : null,
    lastUrl: lastThrottleUrl,
  };
}

export function subscribeThrottle(cb: (snapshot: ThrottleSnapshot) => void): () => void {
  throttleSubscribers.add(cb);
  cb(getThrottleSnapshot());
  return () => throttleSubscribers.delete(cb);
}

function notifyThrottleSubscribers() {
  const snap = getThrottleSnapshot();
  for (const cb of throttleSubscribers) cb(snap);
}

function ensureThrottleWaiter() {
  if (throttleWaiter) return;
  throttleWaiter = new Promise<void>(resolve => {
    throttleWaiterResolve = resolve;
  });
}

function resolveThrottleWaiter() {
  if (throttleWaiterResolve) throttleWaiterResolve();
  throttleWaiter = null;
  throttleWaiterResolve = null;
}

async function waitForThrottleIfNeeded(): Promise<void> {
  const snap = getThrottleSnapshot();
  if (!snap.isThrottled || !snap.throttleUntilMs) return;
  const remaining = Math.max(0, snap.throttleUntilMs - Date.now());
  if (remaining <= 0) return;
  // A single shared waiter prevents N callers from creating N timers.
  ensureThrottleWaiter();
  await throttleWaiter;
}

let queryClientInstance: QueryClient | null = null;

// ESPN-style Alert Component (will be rendered via toast)
function showRateLimitAlert() {
  const event = new CustomEvent('rate-limit-throttled', {
    detail: {
      message: 'SYSTEM COOLING: High traffic detected. Retrying in 5s...',
      cooldownMs: RATE_LIMIT_COOLDOWN_MS,
    },
  });
  window.dispatchEvent(event);
}

function activateThrottleWindow(url?: string) {
  const now = Date.now();
  const nextUntil = now + RATE_LIMIT_COOLDOWN_MS;
  const wasThrottled = getThrottleSnapshot().isThrottled;

  lastThrottleUrl = url;
  throttleUntilMs = Math.max(throttleUntilMs ?? 0, nextUntil);

  if (resumeTimer) clearTimeout(resumeTimer);
  const msUntilResume = Math.max(0, (throttleUntilMs ?? nextUntil) - now);
  resumeTimer = setTimeout(() => {
    throttleUntilMs = null;
    lastThrottleUrl = undefined;
    notifyThrottleSubscribers();
    resolveThrottleWaiter();
  }, msUntilResume);

  notifyThrottleSubscribers();

  // Only show the toast once per throttle window.
  if (!wasThrottled) showRateLimitAlert();
}

// GET request coalescing (spam-refetch protection)
const inFlightGetByUrl = new Map<string, Promise<unknown>>();

async function throwIfResNotOk(res: Response, url?: string) {
  if (!res.ok) {
    // Handle 429 Rate Limiting
    if (res.status === 429) {
      activateThrottleWindow(url);
      throw new Error('429: Rate limit exceeded');
    }

    // Try to parse JSON error response
    let errorMessage = res.statusText;
    let errorBody: unknown;
    try {
      const contentType = res.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const errorData = await res.clone().json();
        errorBody = errorData;
        // Extract error message from common error response formats
        if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
        if (errorData.details != null) {
          const details = errorData.details as Record<string, unknown>;
          const fieldLines =
            details.fieldErrors && typeof details.fieldErrors === 'object'
              ? Object.entries(details.fieldErrors as Record<string, unknown>).flatMap(
                  ([key, value]) =>
                    Array.isArray(value)
                      ? value.map(item => `${key}: ${item}`)
                      : [`${key}: ${String(value)}`]
                )
              : [];
          const detailsText =
            fieldLines.length > 0
              ? fieldLines.join('; ')
              : Array.isArray(errorData.details)
                ? errorData.details.join('; ')
                : typeof errorData.details === 'object'
                  ? Object.entries(details)
                      .flatMap(([key, value]) =>
                        Array.isArray(value)
                          ? value.map(item => `${key}: ${item}`)
                          : [`${key}: ${String(value)}`]
                      )
                      .join('; ')
                  : String(errorData.details);
          if (detailsText) {
            errorMessage = errorMessage ? `${errorMessage} — ${detailsText}` : detailsText;
          }
        }
      } else {
        const text = (await res.clone().text()) || res.statusText;
        errorMessage = text;
      }
    } catch {
      // If JSON parsing fails, fall back to text
      const text = (await res.clone().text()) || res.statusText;
      errorMessage = text;
    }

    // Create error with status code and message
    const error = new Error(errorMessage);
    (error as any).status = res.status;
    (error as any).body = errorBody;
    throw error;
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined
): Promise<Response> {
  await waitForThrottleIfNeeded();

  const doFetch = async () =>
    fetch(url, {
      method,
      headers: data ? { 'Content-Type': 'application/json' } : {},
      body: data ? JSON.stringify(data) : undefined,
      credentials: 'include',
    });

  let res = await doFetch();
  // Never auto-retry auth POSTs on 429 — each retry consumes loginRateLimit budget.
  const isAuthMutation =
    url.includes('/api/auth/login') ||
    url.includes('/api/auth/register') ||
    url.includes('/api/auth/forgot-password') ||
    url.includes('/api/auth/reset-password');
  if (res.status === 429 && !isAuthMutation) {
    activateThrottleWindow(url);
    await waitForThrottleIfNeeded();
    res = await doFetch();
  }

  await throwIfResNotOk(res, url);
  return res;
}

type UnauthorizedBehavior = 'returnNull' | 'throw';
export function getQueryFn<T>(options: {
  on401: UnauthorizedBehavior;
  retry?: number;
}): QueryFunction<T> {
  const { on401: unauthorizedBehavior, retry = 1 } = options;
  return async ({ queryKey }) => {
    // Filter out empty strings to prevent double slashes in URLs
    const filteredKey = queryKey.filter(key => key !== '' && key != null);
    const url = filteredKey.join('/') as string;

    // Guard: If URL contains double slashes, it means a required parameter is missing
    if (url.includes('//')) {
      throw new Error(
        `Invalid query key: missing required parameter. QueryKey: ${JSON.stringify(queryKey)}`
      );
    }

    await waitForThrottleIfNeeded();

    const fetchJson = async (): Promise<T> => {
      const res = await fetch(url, { credentials: 'include' });

      if (unauthorizedBehavior === 'returnNull' && res.status === 401) {
        return null as T;
      }

      if (res.status === 429) {
        activateThrottleWindow(url);
        await waitForThrottleIfNeeded();
        const retryRes = await fetch(url, { credentials: 'include' });

        if (unauthorizedBehavior === 'returnNull' && retryRes.status === 401) {
          return null as T;
        }

        await throwIfResNotOk(retryRes, url);
        return (await retryRes.json()) as T;
      }

      await throwIfResNotOk(res, url);
      return (await res.json()) as T;
    };

    const inFlight = inFlightGetByUrl.get(url);
    if (inFlight) return (await inFlight) as T;

    const p = fetchJson()
      .finally(() => {
        inFlightGetByUrl.delete(url);
      })
      .catch(err => {
        // Ensure callers still see the error, but also clear the coalesced slot.
        throw err;
      });

    inFlightGetByUrl.set(url, p);
    return (await p) as T;
  };
}

// Determine retry count based on route
function getRetryForRoute(queryKey: readonly unknown[]): number {
  const key = queryKey.join('/');
  // Limit retries for high-traffic routes
  if (key.includes('/api/tournaments') || key.includes('/api/organizations')) {
    return 1; // Only 1 retry for tournaments and organizations
  }
  return 1; // Default to 1 retry for all routes
}

// High-Concurrency Architecture: React Query Configuration
// Optimized defaults for high-traffic scenarios (500+ concurrent users)
// TOURNAMENT OPS HARDENING: Aggressive caching to prevent 429 storms
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: context => {
        const retry = getRetryForRoute(context.queryKey);
        return getQueryFn({ on401: 'throw', retry })(context);
      },
      refetchInterval: false,
      // CRITICAL: Stop fetching when switching tabs - prevents request storms
      refetchOnWindowFocus: false,
      // CRITICAL: Don't refetch on reconnect during tournament play
      refetchOnReconnect: false,
      // Global staleTime: 10 minutes (600000ms) - data stays "fresh" longer during tournament play
      staleTime: 600000, // 10 minutes - prevents rate limit loops
      // Cache retention: 15 minutes (900000ms) - keep data in memory longer
      gcTime: 900000, // 15 minutes cache retention (formerly cacheTime)
      retry: (failureCount, error) => {
        // Don't retry on 429 errors
        if (error instanceof Error && error.message.includes('429')) {
          return false;
        }
        // Don't retry auth failures (401) - retrying won't help
        const status = (error as any)?.status;
        if (status === 401) return false;
        // Don't retry server errors (5xx) - indicates backend bug, retrying won't fix
        if (typeof status === 'number' && status >= 500) return false;
        // Use route-specific retry logic for transient errors
        return failureCount < 1; // Max 1 retry
      },
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      retry: false,
    },
  },
});

// Store reference for rate limit handling
queryClientInstance = queryClient;

/** Test-only: clear throttle window + GET coalescing map. */
export function __resetRateLimitThrottleForTests(): void {
  if (resumeTimer) clearTimeout(resumeTimer);
  resumeTimer = null;
  throttleUntilMs = null;
  lastThrottleUrl = undefined;
  resolveThrottleWaiter();
  inFlightGetByUrl.clear();
  notifyThrottleSubscribers();
}

export { RATE_LIMIT_COOLDOWN_MS };
